import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import type { IAccountRepository, IProfileService } from "App/Modules/Auth";
import type { ILanguage, IProfile, IrMap } from "App/Modules/Models";
import type { ILocalesRepository, ITimeZonesRepository } from "App/Modules/Repositories";
import { PatchDelta } from "App/Modules/Services";
import { forkJoin } from "rxjs";
import { UserProfileDetails } from "./UserProfileDetails";
import type { GetDataTransferStatus, SaveDataTransferStatus } from "./Types";

type UserProfileDetailsDataContainerProps = {
    mmProfileService: IProfileService;
    irLocalesRepository: ILocalesRepository;
    mmTimeZonesRepository: ITimeZonesRepository;
    mmAccountRepository: IAccountRepository;
};

export const UserProfileDetailsDataContainer = (props: UserProfileDetailsDataContainerProps) => {
    const [profile, setProfile] = useState<IProfile>();
    const [languages, setLanguages] = useState<ILanguage[]>();
    const [locales, setLocales] = useState<string[]>();
    const [timeZones, setTimeZones] = useState<IrMap<string, string>>();
    const [getProfileStatus, setGetProfileStatus] = useState<GetDataTransferStatus>({
        isLoading: false,
        hasError: false,
    });
    const [saveProfileStatus, setSaveProfileStatus] = useState<SaveDataTransferStatus>({
        isLoading: false,
        hasError: false,
        errorMessage: "",
        hasSavedSuccessfully: false,
    });
    const [getLocalizationStatus, setGetLocalizationStatus] = useState<GetDataTransferStatus>({
        isLoading: false,
        hasError: false,
    });

    const getUserProfileData = () => {
        setGetProfileStatus({
            isLoading: true,
            hasError: false,
        });

        props.mmProfileService.profileObservable().subscribe(
            (profileResult) => {
                setProfile(profileResult);
                setGetProfileStatus({
                    isLoading: false,
                    hasError: false,
                });
            },
            () => {
                setGetProfileStatus({
                    isLoading: false,
                    hasError: true,
                });
            },
        );
    };

    const updateUser = (updatedProfile: IProfile) => {
        const delta = PatchDelta.difference(profile, updatedProfile);

        if (!isEmpty(delta)) {
            setSaveProfileStatus({
                isLoading: true,
                hasError: false,
                errorMessage: "",
                hasSavedSuccessfully: false,
            });

            props.mmProfileService.patch(delta).subscribe(
                () => {
                    if (delta.enableTwoFactorAuth) {
                        props.mmAccountRepository.twoFactorAuthentication(profile.email).subscribe();
                    }

                    setSaveProfileStatus({
                        isLoading: false,
                        hasError: false,
                        errorMessage: "",
                        hasSavedSuccessfully: true,
                    });
                },
                (errorResponse) => {
                    let errorMessage = "";

                    if (errorResponse.error) {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        Object.entries(errorResponse.error.errors).forEach(([key, error]) => {
                            errorMessage = error[0];
                        });
                    }

                    setSaveProfileStatus({
                        isLoading: false,
                        hasError: true,
                        errorMessage,
                        hasSavedSuccessfully: false,
                    });
                },
                () => {},
            );
        }
    };

    const getLocalizationData = () => {
        setGetLocalizationStatus({
            isLoading: true,
            hasError: false,
        });

        const languageObservable = props.mmProfileService.getLanguages();
        const localesObservable = props.irLocalesRepository?.getAllLocales();
        const timeZonesObservable = props.mmTimeZonesRepository?.getAllTimeZones();

        forkJoin({
            languageResponse: languageObservable,
            localesResponse: localesObservable,
            timeZonesResponse: timeZonesObservable,
        }).subscribe(
            (response) => {
                setLanguages(response.languageResponse.data);
                setLocales(response.localesResponse.data);
                setTimeZones(response.timeZonesResponse.data);
                setGetLocalizationStatus({
                    isLoading: false,
                    hasError: false,
                });
            },
            () => {
                setGetLocalizationStatus({
                    isLoading: false,
                    hasError: true,
                });
            },
        );
    };

    const dialogClosed = () => {
        setGetProfileStatus({
            isLoading: false,
            hasError: false,
        });

        setSaveProfileStatus({
            isLoading: false,
            hasError: false,
            errorMessage: "",
            hasSavedSuccessfully: false,
        });

        setGetLocalizationStatus({
            isLoading: false,
            hasError: false,
        });
    };

    const onFormReset = () => {
        setSaveProfileStatus({
            isLoading: false,
            hasError: false,
            errorMessage: "",
            hasSavedSuccessfully: false,
        });
    };

    useEffect(() => {
        getUserProfileData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <UserProfileDetails
            profile={profile}
            languages={languages}
            locales={locales}
            timeZones={timeZones}
            updateUser={updateUser}
            dialogClosed={dialogClosed}
            getLocalizationData={getLocalizationData}
            onFormReset={onFormReset}
            getProfileStatus={getProfileStatus}
            saveProfileStatus={saveProfileStatus}
            getLocalizationStatus={getLocalizationStatus}
        />
    );
};
